import { SiteRoute } from '@journeyapps/package-types';
import styled from '@emotion/styled';
import * as React from 'react';
import { JSX, useCallback, useContext } from 'react';
import { Link } from 'react-router';
import { fade_in_right, fade_in_scale } from '../../animations';
import { Documents, ExternalLinks } from '../../RoutesWidget';
import { IPAD_MEDIA, PHONE_MEDIA, Z_INDEX } from '../../vars';
import { ContentWidget } from '../ContentWidget';
import { LightboxContext } from '../LightboxWrapper';

export interface SectionWidgetProps {
  image: string;
  label: string;
  content: string | JSX.Element;
  ltr?: boolean;
  index: 1 | 2 | 3;
  link?: SiteRoute | ExternalLinks | Documents;
  zoom?: boolean;
  shadow?: boolean;
}

export const SectionWidget: React.FC<SectionWidgetProps> = (props) => {
  const context = useContext(LightboxContext);
  const isZoomable = props.zoom ?? true;
  const hasShadow = props.shadow ?? isZoomable;

  const onClick = useCallback(() => {
    context.setImage(props.image);
  }, [context, props.image]);

  const content = [
    <S.Content index={props.index} key={'content'}>
      <h2>{props.label}</h2>
      {typeof props.content === 'string' ? <S.P>{props.content}</S.P> : props.content}
      {props.link ? (
        <S.P>
          {Object.values(Documents).includes(props.link as Documents) ? (
            <a href={props.link}>Check out the white paper</a>
          ) : (
            <Link to={props.link}>Learn more</Link>
          )}
        </S.P>
      ) : null}
    </S.Content>,
    <S.ImageContainer key="image" ltr={props.ltr ?? true}>
      {isZoomable ? (
        <S.Zoom url={props.image} index={props.index || 1} onClick={onClick} />
      ) : (
        <S.Image src={props.image} index={props.index || 1} shadow={hasShadow} onClick={onClick} />
      )}
    </S.ImageContainer>
  ];

  return <S.Container ltr={props.ltr ?? true}>{(props.ltr ?? true) ? content : content.reverse()}</S.Container>;
};
namespace S {
  const deg = 3;
  const deg_ltr = -3;

  export const ImageContainer = styled.div<{ ltr: boolean }>`
    transform: perspective(200px) rotateY(${(p) => (p.ltr ? deg_ltr : deg)}deg);
    transition: transform 0.5s ease-out;
    &:hover {
      transform: none;
    }

    @media (max-width: ${PHONE_MEDIA}px) {
      min-width: 100%;
      max-width: 100%;
      transform: none;
    }
  `;

  export const Container = styled.div<{ ltr: boolean }>`
    display: flex;
    padding-top: 60px;
    flex-direction: row;
    max-width: 100%;
    column-gap: 50px;
    align-items: center;

    @media (max-width: ${PHONE_MEDIA}px) {
      flex-direction: column;
      row-gap: 20px;
      flex-flow: ${(p) => (p.ltr ? 'column' : 'column-reverse')};
    }
  `;

  export const Content = styled(ContentWidget)<{ index: number }>`
    z-index: ${Z_INDEX.SECTION_CONTENT};
    ${fade_in_right};
    animation-delay: ${(p) => p.index * 0.5}s;
    overflow: auto;

    @media (max-width: ${PHONE_MEDIA}px) {
      max-width: 100%;
    }
  `;

  export const P = styled.p`
    font-size: 16px !important;
  `;

  export const Zoom = styled.div<{ url: string; index: number }>`
    background: url(${(p) => p.url});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 110%;
    min-width: 600px;
    min-height: 300px;
    border-radius: 20px;
    &:hover {
      transition:
        background-size 0.5s ease-out,
        box-shadow 0.5s ease-out;
    }
    box-shadow: 0 0 300px -90px ${(p) => p.theme.section[`imageShadow${p.index}`]};
    cursor: ${(p) => 'pointer'};
    border: solid 1px 'rgba(255, 255, 255, 0.1)';
    ${fade_in_scale};
    animation-delay: ${(p) => p.index * 0.5}s;

    @media (max-width: ${IPAD_MEDIA}px) {
      min-width: 400px;
    }

    @media (max-width: ${PHONE_MEDIA}px) {
      min-width: 100%;
      max-width: 100%;
    }

    &:hover {
      box-shadow: 0 0 300px -100px ${(p) => p.theme.section[`imageShadow${p.index}`]};
      background-size: 100%;
    }
  `;

  export const Image = styled.img<{ index: number; shadow: boolean }>`
    width: 600px;
    min-width: 600px;

    border-radius: 20px;
    box-shadow: 0 0 300px -90px ${(p) => (p.shadow ? p.theme.section[`imageShadow${p.index}`] : 'transparent')} !important;
    cursor: pointer;

    ${fade_in_scale};
    animation-delay: ${(p) => p.index * 0.5}s;

    @media (max-width: ${IPAD_MEDIA}px) {
      min-width: 400px;
    }

    @media (max-width: ${PHONE_MEDIA}px) {
      min-width: 100%;
      max-width: 100%;
    }

    &:hover {
      box-shadow: 0 0 300px -100px ${(p) => (p.shadow ? p.theme.section[`imageShadow${p.index}`] : 'transparent')};
    }
  `;
}
